import { ButtonType } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { textWithPagesElementForm_EditTextWithPagesElementMutation } from "@relay/textWithPagesElementForm_EditTextWithPagesElementMutation.graphql";
import { textWithPagesElementForm_TextWithPagesElementFragment$key } from "@relay/textWithPagesElementForm_TextWithPagesElementFragment.graphql";
import { createTextWithPagesElementFormSchema } from "./text-with-pages-element-form.consts";
import {
	EDIT_TEXT_WITH_PAGES_ELEMENT_MUTATION,
	TEXT_WITH_PAGES_ELEMENT_FRAGMENT,
} from "./text-with-pages-element-form.graphql";
import { backButtonClass } from "./text-with-pages-element-form.styles";
import {
	TextWithPagesElementFormProps,
	TextWithPagesElementFormState,
} from "./text-with-pages-element-form.types";
import { Button, ButtonVariant } from "../../../../components/button";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ValidatedField } from "../../../../components/ValidatedField";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch } from "../../../../store/store.redux";
import { TextWithPagesElementPagesEditor } from "../text-with-pages-element-pages-editor/text-with-pages-element-pages-editor.component";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const TextWithPagesElementForm = ({
	textWithPagesElementFragmentRef,
	onBack,
}: TextWithPagesElementFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const setDraft = useDraftStatusUpdater();
	const toast = useRef<Toast>(null);

	const element = useFragment<textWithPagesElementForm_TextWithPagesElementFragment$key>(
		TEXT_WITH_PAGES_ELEMENT_FRAGMENT,
		textWithPagesElementFragmentRef,
	);
	const [editTextWithPagesElement, isEditingTextWithPagesElement] =
		useMutation<textWithPagesElementForm_EditTextWithPagesElementMutation>(
			EDIT_TEXT_WITH_PAGES_ELEMENT_MUTATION,
		);

	const dispatch = useTypedDispatch();
	const formId = "TextWithPagesElementForm";

	const formik = useFormik<TextWithPagesElementFormState>({
		initialValues: {
			title: element.title,
		},
		validationSchema: createTextWithPagesElementFormSchema(t),
		onSubmit: (values, { setSubmitting }) => {
			editTextWithPagesElement({
				variables: {
					input: {
						elementId: element.id,
						newTitle: values.title,
						newTask: "Unused",
					},
				},
				onCompleted: () => {
					dispatch(resetArrayOfEditedForms());
					setSubmitting(false);
				},
				onError: () => {
					toast.current?.show({
						severity: "error",
						life: 3000,
						summary: t("errors.savingError"),
						detail: t("errors.elementNotSaved"),
					});
					setSubmitting(false);
				},
				updater: setDraft,
			});
		},
	});

	const addToEditedForms = () => {
		dispatch(addEditedFormToEditedFormsArray({ form: formId }));
	};

	return (
		<>
			<Toast ref={toast} />
			<ValidatedField<TextWithPagesElementFormState, string>
				name={"title"}
				label={t("forms.internalTitle")}
				component={DefaultTextFieldComponent}
				formikConfig={formik}
				onChange={addToEditedForms}
			/>
			<Button
				stretch
				disabled={isEditingTextWithPagesElement}
				type={ButtonType.Submit}
				label={t("actions.save")}
				onClick={() => formik.handleSubmit()}
			/>
			<div className={backButtonClass}>
				<Button
					stretch
					variant={ButtonVariant.Strong}
					type={ButtonType.Button}
					onClick={onBack}
					label={t("actions.back")}
				/>
			</div>
			<TextWithPagesElementPagesEditor textWithPagesElementFragmentRef={element} />
		</>
	);
};
